import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../services/api';

interface BSAccount {
  code: string;
  name: string;
  group: string;
  balance: number;
}

interface BSSummary {
  total_assets: number;
  total_liabilities: number;
  total_equity_accounts: number;
  retained_earnings: number;
  total_equity: number;
  total_liabilities_and_equity: number;
  is_balanced: boolean;
  difference: number;
}

interface BSData {
  as_of_date: string | null;
  assets: BSAccount[];
  liabilities: BSAccount[];
  equity: BSAccount[];
  retained_earnings: number;
  summary: BSSummary;
}

const fmt = (n: number) =>
  `LKR ${Math.abs(n).toLocaleString('en-LK', { minimumFractionDigits: 2 })}`;

const BalanceSheet: React.FC = () => {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);

  const [asOfDate, setAsOfDate] = useState(today);
  const [data, setData] = useState<BSData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/accounting/reports/balance-sheet', {
        params: { as_of_date: asOfDate },
      });
      setData(res.data.data);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const s = data?.summary;

  const GroupedSection = ({ accounts }: { accounts: BSAccount[] }) => {
    const groups: Record<string, BSAccount[]> = {};
    accounts.forEach(a => {
      const g = a.group || 'General';
      if (!groups[g]) groups[g] = [];
      groups[g].push(a);
    });

    if (accounts.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={3} style={{ color: '#999', fontStyle: 'italic', padding: '8px' }}>
            No balances
          </TableCell>
        </TableRow>
      );
    }

    return (
      <>
        {Object.entries(groups).map(([group, accts]) => (
          <React.Fragment key={group}>
            {Object.keys(groups).length > 1 && (
              <TableRow>
                <TableCell colSpan={3} style={{ paddingTop: '12px', paddingBottom: '4px', color: '#666', fontSize: '11px', fontStyle: 'italic' }}>
                  {group}
                </TableCell>
              </TableRow>
            )}
            {accts.map(a => (
              <TableRow key={a.code} className="hover">
                <TableCell style={{ fontFamily: 'monospace', fontSize: '12px', color: '#666', width: '80px' }}>{a.code}</TableCell>
                <TableCell>{a.name}</TableCell>
                <TableCell style={{ textAlign: 'right', fontFamily: 'monospace' }}>{fmt(a.balance)}</TableCell>
              </TableRow>
            ))}
          </React.Fragment>
        ))}
      </>
    );
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/accounting/reports')}>← Back</BackButton>
        <div>
          <h1>Balance Sheet</h1>
          <Subtitle>Assets, liabilities and equity at a given date — Assets must equal Liabilities + Equity</Subtitle>
        </div>
      </Header>

      <FilterCard>
        <FilterRow>
          <DateInput
            type="date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
          />
          <LoadButton onClick={load} disabled={loading}>
            {loading ? 'Loading...' : 'Load Report'}
          </LoadButton>
          {data && (
            <ExportButton onClick={() => window.print()}>
              Export PDF
            </ExportButton>
          )}
        </FilterRow>
      </FilterCard>

      {error && <ErrorAlert>{error}</ErrorAlert>}

      {!data && !loading && (
        <EmptyState>
          <EmptyIcon>📊</EmptyIcon>
          <p>Select a date and click "Load Report" to view the Balance Sheet</p>
        </EmptyState>
      )}

      {data && s && (
        <ReportCard>
          <ReportHeader>
            <ReportTitle>Balance Sheet</ReportTitle>
            <ReportDate>As of {data.as_of_date || asOfDate}</ReportDate>
          </ReportHeader>

          {/* Summary Cards */}
          <SummaryGrid>
            <SummaryCard color="#1565c0">
              <SummaryLabel>Total Assets</SummaryLabel>
              <SummaryValue>{fmt(s.total_assets)}</SummaryValue>
            </SummaryCard>
            <SummaryCard color="#c62828">
              <SummaryLabel>Total Liabilities</SummaryLabel>
              <SummaryValue>{fmt(s.total_liabilities)}</SummaryValue>
            </SummaryCard>
            <SummaryCard color="#2e7d32">
              <SummaryLabel>Total Equity</SummaryLabel>
              <SummaryValue>{fmt(s.total_equity)}</SummaryValue>
            </SummaryCard>
            <SummaryCard color={s.is_balanced ? '#2e7d32' : '#c62828'}>
              <SummaryLabel>Balance Check</SummaryLabel>
              <SummaryValue>{s.is_balanced ? '✓ Balanced' : '✗ Not Balanced'}</SummaryValue>
            </SummaryCard>
          </SummaryGrid>

          {!s.is_balanced && (
            <WarningAlert>
              ⚠️ Balance Sheet is not balanced! Difference: {fmt(s.difference)}
            </WarningAlert>
          )}

          <Table>
            <tbody>
              {/* ASSETS */}
              <SectionHeader>
                <TableCell colSpan={3}>ASSETS</TableCell>
              </SectionHeader>
              <GroupedSection accounts={data.assets} />
              <TotalRow highlight="assets">
                <TableCell colSpan={2}>Total Assets</TableCell>
                <TableCell style={{ textAlign: 'right', fontFamily: 'monospace' }}>{fmt(s.total_assets)}</TableCell>
              </TotalRow>

              {/* LIABILITIES */}
              <SectionHeader>
                <TableCell colSpan={3}>LIABILITIES</TableCell>
              </SectionHeader>
              <GroupedSection accounts={data.liabilities} />
              <TotalRow highlight="liab">
                <TableCell colSpan={2}>Total Liabilities</TableCell>
                <TableCell style={{ textAlign: 'right', fontFamily: 'monospace' }}>{fmt(s.total_liabilities)}</TableCell>
              </TotalRow>

              {/* EQUITY */}
              <SectionHeader>
                <TableCell colSpan={3}>EQUITY</TableCell>
              </SectionHeader>
              <GroupedSection accounts={data.equity} />
              <TableRow className="hover">
                <TableCell style={{ fontFamily: 'monospace', fontSize: '12px', color: '#666', width: '80px' }}>9999</TableCell>
                <TableCell>Retained Earnings (Net Profit/Loss)</TableCell>
                <TableCell style={{ textAlign: 'right', fontFamily: 'monospace' }}>{fmt(s.retained_earnings)}</TableCell>
              </TableRow>
              <TotalRow highlight="equity">
                <TableCell colSpan={2}>Total Equity</TableCell>
                <TableCell style={{ textAlign: 'right', fontFamily: 'monospace' }}>{fmt(s.total_equity)}</TableCell>
              </TotalRow>

              {/* GRAND TOTAL */}
              <GrandTotalRow highlight={s.is_balanced ? 'balanced' : 'error'}>
                <TableCell colSpan={2}>Total Liabilities and Equity</TableCell>
                <TableCell style={{ textAlign: 'right', fontFamily: 'monospace' }}>{fmt(s.total_liabilities_and_equity)}</TableCell>
              </GrandTotalRow>
            </tbody>
          </Table>
        </ReportCard>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;

  h1 {
    font-size: 28px;
    color: #333;
    margin-bottom: 5px;
  }
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 14px;
`;

const BackButton = styled.button`
  background: white;
  border: 1px solid #ddd;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #667eea;
    color: #667eea;
  }
`;

const FilterCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const DateInput = styled.input`
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  min-width: 160px;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const LoadButton = styled.button`
  padding: 10px 24px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #5568d3;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ExportButton = styled.button`
  padding: 10px 24px;
  background: white;
  color: #667eea;
  border: 2px solid #667eea;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #667eea;
    color: white;
  }
`;

const ErrorAlert = styled.div`
  background: #fee;
  color: #c00;
  padding: 15px 20px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid #fcc;
`;

const WarningAlert = styled.div`
  background: #fff3cd;
  color: #856404;
  padding: 15px 20px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid #ffeaa7;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #666;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  opacity: 0.3;
  margin-bottom: 20px;
`;

const ReportCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const ReportHeader = styled.div`
  padding: 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  text-align: center;
`;

const ReportTitle = styled.h2`
  font-size: 24px;
  margin-bottom: 8px;
`;

const ReportDate = styled.div`
  opacity: 0.9;
  font-size: 14px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  padding: 30px;
  background: #f8f9fa;
`;

const SummaryCard = styled.div<{ color: string }>`
  background: white;
  padding: 20px;
  border-radius: 8px;
  border-left: 4px solid ${props => props.color};
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #333;
  font-family: monospace;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableRow = styled.tr`
  &.hover:hover {
    background: #f5f5f5;
  }
`;

const TableCell = styled.td`
  padding: 12px 20px;
  border-bottom: 1px solid #eee;
`;

const SectionHeader = styled.tr`
  background: #f0f0f0;

  td {
    font-weight: 700;
    text-transform: uppercase;
    font-size: 12px;
    letter-spacing: 1px;
    color: #666;
    padding: 10px 20px;
  }
`;

const TotalRow = styled.tr<{ highlight?: string }>`
  background: ${props => {
    const bgMap: Record<string, string> = {
      assets: 'rgba(21,101,192,0.1)',
      liab: 'rgba(198,40,40,0.08)',
      equity: 'rgba(46,125,50,0.1)',
    };
    return props.highlight ? bgMap[props.highlight] : '#f5f5f5';
  }};

  td {
    font-weight: 700;
    font-size: 14px;
    padding: 12px 20px;
    color: ${props => {
      const colorMap: Record<string, string> = {
        assets: '#1565c0',
        liab: '#c62828',
        equity: '#2e7d32',
      };
      return props.highlight ? colorMap[props.highlight] : 'inherit';
    }};
  }
`;

const GrandTotalRow = styled.tr<{ highlight?: string }>`
  background: ${props => (props.highlight === 'balanced' ? 'rgba(46,125,50,0.15)' : 'rgba(198,40,40,0.1)')};

  td {
    font-weight: 800;
    font-size: 15px;
    padding: 15px 20px;
    color: ${props => (props.highlight === 'balanced' ? '#2e7d32' : '#c62828')};
  }
`;

export default BalanceSheet;
